/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1751932800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 08 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "cadent",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Cadent",
          "depth": 0,
          "id": "connect-to-cadent"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing conversions",
          "depth": 1,
          "id": "syncing-conversions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Conversions"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion event data to Cadent"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-cadent",
        children: "Connect to Cadent"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Cadent"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Cadent"
        }), " by entering your API key."]
      }), "\n", _jsx(_components.p, {
        children: "If you do not have an API key, contact Cadent support to set one up."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Cadent destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Cadent destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversions",
        children: "Syncing conversions"
      }), "\n", _jsx(_components.p, {
        children: "Sync event conversions data to Cadent."
      }), "\n", _jsx(_components.h4, {
        id: "dataset-selection",
        children: "Dataset selection"
      }), "\n", _jsx(_components.p, {
        children: "Enter the dataset ID you would like to sync events to. This ID can be found on the Dataset Details page in Cadent."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Each Cadent dataset is set up with a match key. Include this match key in your mappings to successfully sync events to Cadent."
      }), "\n", _jsx(_components.p, {
        children: "Provide the event name, and optionally provide the event timestamp. If no timestamp is provided, Cadent will use the time the event was received."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties and user properties via field mappings. Select the column in your model you'd like to map to each field in Cadent you'd like to sync."
      }), "\n", _jsx(_components.p, {
        children: "The following are valid values for the event conversion type in Cadent:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "email_clickthrough"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "site_clickthrough"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "online_purchase"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "app_install"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "in_app_purchase"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "physical_store_visit"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "offline_purchase"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "phone_call"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "chat_event"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "system_generated"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-hashing",
        children: "Field hashing"
      }), "\n", _jsx(_components.p, {
        children: "The following fields are required to be hashed, and hashed by default:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Email (", _jsx(_components.code, {
            children: "em"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["First name (", _jsx(_components.code, {
            children: "fn"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Last name (", _jsx(_components.code, {
            children: "ln"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Date of birth (", _jsx(_components.code, {
            children: "db"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Gender (", _jsx(_components.code, {
            children: "ge"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["City (", _jsx(_components.code, {
            children: "ct"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["State (", _jsx(_components.code, {
            children: "st"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Zip code (", _jsx(_components.code, {
            children: "zp"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Country (", _jsx(_components.code, {
            children: "country"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["External ID (", _jsx(_components.code, {
            children: "external-id"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If the data in your model is already hashed, you can disable field hashing in your sync."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
